import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { IWarehouseRecord } from "../../../models/warehouse.model";
import PaginationControls from "../../../utils/PaginationControls";

interface WarehousesSectionProps {
  warehouses: IWarehouseRecord[];
  totalWarehouseItems: number;
  totalWarehousePages: number;
  warehousePage: number;
  itemsPerPage: number;
  handleEdit: (type: "factory", item: IWarehouseRecord) => void;
  handleWarehousePageChange: (page: number) => void;
  handleDelete: (
    type: "factory" | "vendor" | "material",
    id: string,
    name: string,
  ) => void;
}

const IconWrapper: React.FC<{
  icon: React.ElementType;
  className?: string;
}> = ({ icon: Icon, className }) => <Icon className={className} />;

const Button: React.FC<{
  type: string;
  onClick: () => void;
  className: string;
  children: React.ReactNode;
  disabled?: boolean;
}> = ({ type, onClick, className, children, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-md font-medium transition-colors ${className}`}
    disabled={disabled}
  >
    {children}
  </button>
);

const WarehousesSection: React.FC<WarehousesSectionProps> = ({
  warehouses,
  totalWarehouseItems,
  totalWarehousePages,
  warehousePage,
  itemsPerPage,
  handleEdit,
  handleDelete,
  handleWarehousePageChange,
}) => {
  const { allUsersNames } = useSelector((state: RootState) => state.users);

  return (
    <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 table-fixed w-full">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3"
            >
              Name
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3"
            >
              Address
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6"
            >
              Contact Person
            </th>
            <th scope="col" className="px-6 py-3 text-right w-1/6">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {(warehouses || []).map((warehouse: IWarehouseRecord) => (
            <tr key={warehouse._id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <IconWrapper
                      icon={() => <span>🏠</span>}
                      className="h-5 w-5 text-indigo-600"
                    />
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900 whitespace-normal">
                      {warehouse.name}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900 whitespace-normal">
                  {warehouse.address}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {allUsersNames.find(
                  (user) => user._id === String(warehouse.contactPerson),
                )?.username || "NA"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  className="text-indigo-600 hover:text-indigo-900 mr-4"
                  onClick={() => handleEdit("factory", warehouse)}
                >
                  <IconWrapper icon={() => <span>✏️</span>} />
                </button>
                <button
                  className="text-red-600 hover:text-red-900"
                  onClick={() =>
                    handleDelete(
                      "factory",
                      String(warehouse._id),
                      warehouse.name ?? "",
                    )
                  }
                >
                  <IconWrapper icon={() => <span>🗑️</span>} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-gray-200">
        <PaginationControls
          currentPage={warehousePage}
          totalPages={totalWarehousePages}
          onPageChange={handleWarehousePageChange}
          totalItems={totalWarehouseItems}
        />
      </div>
    </div>
  );
};

export default WarehousesSection;
